import { useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import type { IrrigationDevice } from '@/lib/api/irrigation-devices-api'
import type { IrrigationRunFormValues } from '@/lib/schemas/irrigation-schemas'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CameraSwitchView } from '@/components/cameras/camera-switch-view'
import { useIrrigationDeviceCameras } from '@/hooks/use-cameras-query'
import { useCreateIrrigationRun } from '@/hooks/use-irrigation-devices-query'
import { ApiError } from '@/lib/api/client'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { fieldOfApiError } from '@/lib/irrigation-schedule'
import { m } from '@/lib/paraglide/messages'
import { createIrrigationRunSchema } from '@/lib/schemas/irrigation-schemas'
import { fromVnInput, toVnInput } from '@/lib/work-activity'

const DEFAULT_MINUTES = 15
/** Hẹn giờ mặc định: 10 phút nữa, để người dùng chỉnh từ một mốc hợp lý. */
const DEFAULT_LEAD_MS = 10 * 60_000

interface Props {
    device: IrrigationDevice
    open: boolean
    onOpenChange: (open: boolean) => void
    /**
     * Hiện luồng camera của trạm cạnh form (mặc định có). Tắt khi hộp thoại
     * được mở từ khung xem trực tiếp — ở đó đã có luồng, mở thêm là hai kết
     * nối tới cùng một camera.
     */
    showCamera?: boolean
}

/**
 * Bật bơm luôn kèm thời lượng: tưới ngay X phút, hoặc hẹn giờ một lần. Hệ
 * thống tự tắt khi hết giờ. Lệnh THẬT chỉ gửi khi bấm nút xác nhận.
 */
export function IrrigationRunDialog({
    device,
    open,
    onOpenChange,
    showCamera = true,
}: Props) {
    const create = useCreateIrrigationRun()
    const { data: cameras = [] } = useIrrigationDeviceCameras(device.id)
    // Nhìn thấy máy bơm và đường ống trước khi bấm bật.
    const withCamera = showCamera && cameras.length > 0
    const form = useForm<IrrigationRunFormValues>({
        resolver: zodResolver(createIrrigationRunSchema()),
        defaultValues: {
            mode: 'now',
            duration_minutes: DEFAULT_MINUTES,
            start_at: '',
        },
    })

    useEffect(() => {
        if (!open) return
        form.reset({
            mode: 'now',
            duration_minutes: DEFAULT_MINUTES,
            start_at: toVnInput(
                new Date(Date.now() + DEFAULT_LEAD_MS).toISOString(),
            ),
        })
        // form là ref ổn định của RHF; chỉ reset mỗi lần mở hộp thoại.
    }, [open, form])

    const mode = form.watch('mode')

    const submit = form.handleSubmit(async (values) => {
        try {
            await create.mutateAsync({
                device,
                startAt:
                    values.mode === 'once'
                        ? fromVnInput(values.start_at)
                        : null,
                durationMinutes: values.duration_minutes,
            })
            onOpenChange(false)
        } catch (err) {
            // Toast đã báo lý do; lỗi thuộc một ô thì gắn thêm vào ô đó và giữ
            // hộp thoại mở để sửa.
            const field = fieldOfApiError(err)
            if (
                err instanceof ApiError &&
                (field === 'duration_minutes' || field === 'start_at')
            ) {
                form.setError(field, {
                    message: resolveApiErrorMessage(err.code) ?? undefined,
                })
            }
        }
    })

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                // Không cho đóng giữa chừng khi lệnh đang gửi.
                if (!create.isPending) onOpenChange(next)
            }}
        >
            <DialogContent
                className={
                    withCamera
                        ? 'max-h-[calc(100svh-2rem)] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-5xl'
                        : undefined
                }
            >
                <div
                    className={
                        withCamera
                            ? 'grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]'
                            : undefined
                    }
                >
                    {withCamera && (
                        <CameraSwitchView
                            cameras={cameras}
                            mode="live"
                            active={open}
                            className="aspect-video w-full self-start overflow-hidden rounded-md"
                        />
                    )}
                    <FormProvider {...form}>
                        <form
                            noValidate
                            onSubmit={(e) => void submit(e)}
                            className="space-y-4"
                        >
                            <DialogHeader>
                                <DialogTitle>
                                    {m.irrigation_run_dialog_title({
                                        name: device.name,
                                    })}
                                </DialogTitle>
                                <DialogDescription>
                                    {mode === 'now'
                                        ? m.irrigation_run_now_hint()
                                        : m.irrigation_run_once_hint()}
                                </DialogDescription>
                            </DialogHeader>

                            <Controller
                                control={form.control}
                                name="mode"
                                render={({ field }) => (
                                    <Tabs
                                        value={field.value}
                                        onValueChange={field.onChange}
                                    >
                                        <TabsList className="w-full">
                                            <TabsTrigger
                                                value="now"
                                                className="flex-1"
                                            >
                                                {m.irrigation_run_mode_now()}
                                            </TabsTrigger>
                                            <TabsTrigger
                                                value="once"
                                                className="flex-1"
                                            >
                                                {m.irrigation_run_mode_once()}
                                            </TabsTrigger>
                                        </TabsList>
                                    </Tabs>
                                )}
                            />

                            {mode === 'once' && (
                                <Field
                                    name="start_at"
                                    label={m.irrigation_run_start_at()}
                                    required
                                >
                                    <Input
                                        id="start_at"
                                        type="datetime-local"
                                        {...form.register('start_at')}
                                    />
                                </Field>
                            )}
                            <Field
                                name="duration_minutes"
                                label={m.irrigation_run_duration()}
                                required
                            >
                                <Input
                                    id="duration_minutes"
                                    inputMode="numeric"
                                    {...form.register('duration_minutes')}
                                />
                            </Field>

                            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                                <li>
                                    {m.irrigation_pump_confirm_manual_mode()}
                                </li>
                                <li>
                                    {m.irrigation_pump_confirm_state_delay()}
                                </li>
                                <li>{m.irrigation_pump_confirm_cooldown()}</li>
                            </ol>

                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => onOpenChange(false)}
                                    disabled={create.isPending}
                                >
                                    {m.irrigation_pump_confirm_cancel()}
                                </Button>
                                {/* Chỉ disable khi đang gửi (chống bấm hai lần). */}
                                <Button
                                    type="submit"
                                    disabled={create.isPending}
                                >
                                    {create.isPending
                                        ? m.irrigation_pump_sending()
                                        : mode === 'now'
                                          ? m.irrigation_run_submit_now()
                                          : m.irrigation_run_submit_once()}
                                </Button>
                            </DialogFooter>
                        </form>
                    </FormProvider>
                </div>
            </DialogContent>
        </Dialog>
    )
}
